import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AlarmClock,
  Activity,
  Blocks,
  BookOpen,
  Brain,
  Check,
  Copy,
  ExternalLink,
  History,
  Link2,
  MessageCircle,
  Mic,
  Sparkles,
  X,
  Zap,
} from "lucide-react";
import { createBooSession, getAssistant, getAssistantNativeClientPlatforms } from "../../../services/assistant";
import Loading from "../../../components/loading";
import { getDashboard } from "../../../services/dashboard";
import dayjs from "dayjs";
import AssistantDashboard from "./components/AssistantDashboard";
import Documents from "./Knowledge";
import Integrations from "./Integrations";
import Triggers from "./Triggers";
import Historic from "./Historic";
import UserContext from "../../../context/users";
import { AdminRole } from "../../../constants";
import Tasks from "./Tasks";
import Editor from "./Editor";
import GenerativeChatTest from "../../../components/GenerativeChatTest";
import RealtimeVoiceTest from "../../../components/RealtimeVoiceTest";
import { TabsList } from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Tooltip } from "../../../components/ui/tooltip";
import { EmptyState } from "../../../components/ui/empty-state";
import { Dialog, DialogHeader, DialogContent } from "../../../components/ui/dialog";
import ClientsManager from "./components/ClientsManager";
import { toast } from 'sonner'
import { cn } from "../../../lib/cn"

const defaultDate = [dayjs().subtract(7, "day"), dayjs()];

export default function EditGenerative() {
  const location = useLocation();
  const { id } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState({});
  const [graphLoading, setGraphLoading] = useState(true);
  const [user] = useContext(UserContext);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMode, setChatMode] = useState("text");
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [linkedClientsCount, setLinkedClientsCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    getAssistantNativeClientPlatforms(id)
      .then(({ data }) => setLinkedClientsCount(data?.length || 0))
      .catch((e) => console.error(e));
  }, [id]);

  const handleClientsChange = useCallback((list) => {
    setLinkedClientsCount(list?.length || 0);
  }, []);

  const locationSplit = location?.pathname?.split("/");

  const loadGraph = useCallback(
    (graphId) => {
      if (!graphId) return;

      const filters = {
        serviceID: graphId,
        service: "ASSISTANT",
        startDate: defaultDate[0].toISOString().substring(0, 10) + "T00:00:00Z",
        endDate: defaultDate[1].toISOString().substring(0, 10) + "T23:59:59Z",
      };

      setGraphLoading(true);
      getDashboard(filters)
        .then(({ data: graph }) => setGraphData(graph))
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao carregar dashboard");
        })
        .finally(() => setGraphLoading(false));
    },
    []
  );

  useEffect(() => {
    setLoading(true);
    getAssistant(id)
      .then(({ data: assistant }) => {
        setData({ ...assistant });
        document.title = "Verboo | " + assistant?.name;
        loadGraph(assistant?.id);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Falha ao carregar informações do chatbot");
      })
      .finally(() => setLoading(false));

    return () => {
      document.title = "Verboo";
    };
  }, [id, loadGraph]);

  const tabs = useMemo(() => {
    const baseItems = [
      {
        label:
          data?.name?.length > 14
            ? data?.name?.substring(0, 14) + "…"
            : data?.name ?? "Início",
        key: String(data.id),
        icon: Brain,
      },
      { label: "Conhecimento", key: "knowledge", icon: BookOpen },
      { label: "Integrações", key: "integrations", icon: Blocks },
      { label: "Gatilhos", key: "triggers", icon: Zap },
      { label: "Histórico", key: "historic", icon: History },
      { label: "Tarefas", key: "tasks", icon: AlarmClock },
    ];

    return user?.role !== AdminRole
      ? baseItems.filter((item) => item.key !== "tasks")
      : baseItems;
  }, [data?.name, user?.role, data?.id]);

  const selectedKey = useMemo(
    () => locationSplit[locationSplit?.length - 1],
    [locationSplit]
  );

  const onTabChange = useCallback(
    (key) => navigate(key === id ? "" : key),
    [navigate, id]
  );

  if (loading) {
    return <Loading title={"Carregando..."} />;
  }

  return (
    <div
      className="dash-scroll flex h-full w-full flex-col overflow-y-auto"
      style={{ background: "var(--dash-bg)" }}
    >
      <AssistantHeader
        data={data}
        clientsCount={linkedClientsCount}
        activeTab={selectedKey}
        onOpenBoo={async () => {
          const booWindow = window.open("about:blank", "boo-admin")
          try {
            const response = await createBooSession(id)
            const ticket = response?.data?.ticket
            const booUrl = import.meta.env.VITE_BOO_ADMIN_URL

            if (!ticket || !booUrl || !booWindow) {
              throw new Error("Não foi possível iniciar a sessão no Boo")
            }

            const booBaseUrl = booUrl.replace(/\/chat\/?$/, "").replace(/\/$/, "")

            const form = document.createElement("form")
            form.method = "POST"
            form.action = `${booBaseUrl}/chat/sso`
            form.target = "boo-admin"
            form.style.display = "none"

            for (const [name, value] of Object.entries({
              ticket,
              assistantId: String(id),
              locale: "pt",
            })) {
              const input = document.createElement("input")
              input.type = "hidden"
              input.name = name
              input.value = value
              form.appendChild(input)
            }

            document.body.appendChild(form)
            form.submit()
            form.remove()
          } catch (error) {
            booWindow?.close()
            toast.error(error?.data?.message || error?.message || "Falha ao abrir o Boo")
          }
        }}
        onOpenChat={() => {
          setChatMode("text");
          setChatOpen(true);
        }}
        onOpenDashboard={() => setDashboardOpen(true)}
        onOpenClients={() => setClientsOpen(true)}
      />

      <div
        className="sticky top-0 z-30 border-b"
        style={{
          background: "var(--dash-bg)",
          borderColor: "var(--dash-border)",
        }}
      >
        <div className="mx-auto w-full max-w-[1400px] px-4 md:px-6">
          <TabsList items={tabs} value={selectedKey} onChange={onTabChange} />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-4 md:px-6 md:py-6">
        <Routes>
          <Route
            index
            path={"/"}
            element={
              <Editor
                data={data}
                setData={setData}
                assistantId={id}
                onOpenChat={(mode = "text") => {
                  setChatMode(mode);
                  setChatOpen(true);
                }}
              />
            }
          />
          <Route path={"/knowledge"} element={<Documents id={id} />} />
          <Route
            path={"/integrations"}
            element={<Integrations id={id} />}
          />
          <Route path={"/historic/*"} element={<Historic assistantId={id} />} />
          <Route path={"/triggers/*"} element={<Triggers assistantId={id} />} />
          <Route path={"/tasks/*"} element={<Tasks assistantId={id} />} />
          <Route
            path={"*"}
            element={
              <EmptyState
                icon={Sparkles}
                title="Em construção"
                description="Esta seção ainda não está disponível."
              />
            }
          />
        </Routes>
      </div>

      {chatOpen && (
        <ChatPanel
          assistantId={id}
          assistant={data}
          initialMode={chatMode}
          onClose={() => setChatOpen(false)}
        />
      )}

      <ClientsManager
        open={clientsOpen}
        onClose={() => setClientsOpen(false)}
        assistant={data}
        onAssociationChange={handleClientsChange}
      />

      <Dialog
        open={dashboardOpen}
        onClose={() => setDashboardOpen(false)}
        size="3xl"
      >
        <DialogHeader
          icon={Activity}
          title="Dashboard do assistente"
          description={`Performance e métricas · ${data?.name || "—"}`}
        />
        <DialogContent>
          <div className="dash-scroll max-h-[72vh] overflow-y-auto pb-4 pr-1">
            <AssistantDashboard data={graphData} loading={graphLoading} />
          </div>
        </DialogContent>
        <div className="h-4" />
      </Dialog>
    </div>
  );
}

const DOCS_BY_TAB = {
  integrations: 'https://verboo.ai/pt/docs/integrations/overview',
  triggers: 'https://verboo.ai/pt/docs/concepts/triggers',
  knowledge: 'https://verboo.ai/pt/docs/concepts/knowledge-base',
  historic: 'https://verboo.ai/pt/docs/concepts/historic',
};
const DOCS_DEFAULT = 'https://verboo.ai/pt/docs/concepts/generative-agents';

function AssistantHeader({
  data,
  clientsCount = 0,
  activeTab,
  onOpenBoo,
  onOpenChat,
  onOpenDashboard,
  onOpenClients,
}) {
  const [copied, setCopied] = useState(false);
  const docsUrl = DOCS_BY_TAB[activeTab] ?? DOCS_DEFAULT;

  function copyId() {
    navigator.clipboard.writeText(String(data.id));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div
      className="relative border-b"
      style={{
        background: "var(--dash-surface)",
        borderColor: "var(--dash-border)",
      }}
    >
      <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6 md:py-6">
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <div
              className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl md:h-14 md:w-14 md:rounded-2xl"
              style={{
                background: "var(--dash-primary-soft)",
                border: "1px solid var(--dash-primary-border)",
              }}
            >
              <Brain
                size={20}
                strokeWidth={1.8}
                style={{ color: "var(--dash-primary)" }}
                className="md:hidden"
              />
              <Brain
                size={24}
                strokeWidth={1.8}
                style={{ color: "var(--dash-primary)" }}
                className="hidden md:block"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1 md:gap-1.5">
              <div className="flex items-center gap-2">
                <h1
                  className="truncate text-lg font-semibold tracking-tight md:text-2xl"
                  style={{ color: "var(--dash-text)" }}
                >
                  {data?.name || "Assistente sem nome"}
                </h1>
                <Badge variant={data?.isActive ? "success" : "muted"} dot>
                  {data?.isActive ? "Ativo" : "Inativo"}
                </Badge>
              </div>
              <button
                type="button"
                onClick={copyId}
                className="group flex w-fit items-center gap-1.5 rounded-md px-2 py-0.5 font-mono text-[11px] transition-colors"
                style={{
                  background: "var(--dash-surface-subtle)",
                  border: "1px solid var(--dash-border)",
                  color: "var(--dash-text-muted)",
                }}
              >
                <span>ID: {data.id}</span>
                {copied ? (
                  <Check
                    size={11}
                    strokeWidth={2.6}
                    style={{ color: "var(--dash-primary)" }}
                  />
                ) : (
                  <Copy
                    size={11}
                    strokeWidth={2}
                    className="transition-opacity group-hover:opacity-100"
                  />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto">
            <Tooltip content="Ver documentação">
              <a href={docsUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" leftIcon={ExternalLink}>
                  Docs
                </Button>
              </a>
            </Tooltip>
            <Tooltip
              content={
                clientsCount > 0
                  ? `${clientsCount} cliente${clientsCount > 1 ? "s" : ""} vinculado${clientsCount > 1 ? "s" : ""}`
                  : "Vincular a um cliente"
              }
            >
              <Button
                variant="outline"
                leftIcon={Link2}
                onClick={onOpenClients}
              >
                Clientes
                {clientsCount > 0 && (
                  <span
                    className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums"
                    style={{
                      background: "var(--dash-primary)",
                      color: "#fff",
                    }}
                  >
                    {clientsCount}
                  </span>
                )}
              </Button>
            </Tooltip>
            <Tooltip content="Métricas e dashboard">
              <Button
                variant="outline"
                leftIcon={Activity}
                onClick={onOpenDashboard}
              >
                Dashboard
              </Button>
            </Tooltip>
            <Tooltip content="Abrir atendimento no Boo">
              <Button
                variant="outline"
                leftIcon={MessageCircle}
                onClick={onOpenBoo}
              >
                Abrir Boo
              </Button>
            </Tooltip>
            <Button
              variant="primary"
              leftIcon={MessageCircle}
              onClick={onOpenChat}
            >
              Testar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatPanel({ assistantId, assistant, initialMode = "text", onClose }) {
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col overflow-hidden rounded-2xl",
        "bg-[var(--dash-surface)] border border-[var(--dash-border-strong)]",
        "shadow-[0_24px_64px_rgba(0,0,0,0.45),0_0_0_1px_var(--dash-primary-border)]",
        "animate-[chat-panel-in_0.25s_ease-out]",
        "max-md:left-3 max-md:right-3 max-md:top-4 max-md:bottom-48",
        "md:right-6 md:bottom-24 md:h-[min(640px,calc(100vh-160px))] md:w-[min(420px,calc(100vw-32px))]",
      )}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--dash-border)] px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--dash-primary-soft)] border border-[var(--dash-primary-border)] text-[var(--dash-primary)]">
            {mode === "voice" ? <Mic size={13} strokeWidth={2.2} /> : <MessageCircle size={13} strokeWidth={2.2} />}
          </div>
          <div className="flex flex-col">
            <span className="text-xs font-semibold leading-none text-[var(--dash-text)]">
              Testar assistente
            </span>
            <span className="mt-0.5 text-[10px] text-[var(--dash-text-muted)]">
              {mode === "voice" ? "WebRTC · baixa latência" : "Conversa por texto"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--dash-surface-subtle)] border border-[var(--dash-border)] text-[var(--dash-text-soft)] transition-colors"
          aria-label="Fechar"
        >
          <X size={13} />
        </button>
      </div>
      <div className="grid shrink-0 grid-cols-2 gap-1 border-b border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-1.5">
        <button
          type="button"
          onClick={() => setMode("text")}
          className={cn(
            "flex h-8 items-center justify-center gap-1.5 rounded-lg text-[11px] font-medium transition-colors",
            mode === "text"
              ? "bg-[var(--dash-surface)] text-[var(--dash-primary)] shadow-sm"
              : "text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]",
          )}
        >
          <MessageCircle size={12} />
          Texto
        </button>
        <button
          type="button"
          onClick={() => setMode("voice")}
          className={cn(
            "flex h-8 items-center justify-center gap-1.5 rounded-lg text-[11px] font-medium transition-colors",
            mode === "voice"
              ? "bg-[var(--dash-surface)] text-[var(--dash-primary)] shadow-sm"
              : "text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]",
          )}
        >
          <Mic size={12} />
          Voz ao vivo
        </button>
      </div>
      <div className="min-h-0 flex-1 p-3">
        {mode === "voice" ? (
          <RealtimeVoiceTest assistantId={assistantId} assistant={assistant} />
        ) : (
          <GenerativeChatTest assistantId={assistantId} />
        )}
      </div>
    </div>
  );
}
